import { lazy, Suspense, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../components/DataTable'
import { adminFinance } from '../api/finance'
import type { CreditCurrencyRow, FinanceOverview, MoneyEntry, ProjectRow, SourceEntry } from '../api/financeTypes'
import { PERIODS, periodRange, type PeriodKey } from '../lib/financeMode'
import { reasonFor } from '../lib/financeErrors'
import { DASH, decimalSortKey, entryFor, formatAmount, formatCount, formatMoney, formatPercent, percentChange, primarySortKey, ratioPermille } from '../lib/money'
import { formatDateTime } from '../lib/format'
import { useFinance } from './financeContext'
import { SEVERITY, SOURCE_LABEL, moneyJoin, problemTitle, shortDate } from './labels'
import {
  Card,
  Chip,
  ColumnHelpHeader,
  ColumnHelpPanel,
  FinanceErrorNotice,
  FinanceQuery,
  HelpLabel,
  MoneyLines,
  NullValue,
  PageHead,
  Segmented,
} from './ui'

// echarts is large: loaded only when the overview shows the chart.
const RevenueChart = lazy(() => import('./RevenueChart'))

function Delta({ cur, prev }: { cur: MoneyEntry[] | null | undefined; prev: MoneyEntry[] | null | undefined }) {
  if (!cur || cur.length === 0) return null
  return (
    <>
      {cur.map((c) => {
        const p = percentChange(c.amount, entryFor(prev, c.currency)?.amount)
        if (p === null) return null
        return (
          <span key={c.currency} className={p >= 0 ? 'af-up' : 'af-down'}>
            {formatPercent(p)}
            {cur.length > 1 ? ` ${c.currency}` : ''}{' '}
          </span>
        )
      })}
    </>
  )
}

function Kpi({ helpKey, label, tone, children, sub }: { helpKey: string; label: string; tone?: 'crit'; children: ReactNode; sub: ReactNode }) {
  return (
    <div className="af-card af-kpi">
      <HelpLabel helpKey={helpKey} className="af-lbl">
        {label}
      </HelpLabel>
      <div className={`af-kv af-num${tone ? ` af-kv--${tone}` : ''}`}>{children}</div>
      <div className="af-kpi__sub">{sub}</div>
    </div>
  )
}

function KpiGrid({ d }: { d: FinanceOverview }) {
  const err = d.errors
  const subs = d.subscriptions
  const unknown = d.unknown_outcome?.count
  return (
    <section aria-label="Fő mutatók" className="af-kpis">
      <Kpi
        helpKey="revenue"
        label="Befolyt bevétel"
        sub={
          <>
            <Delta cur={d.revenue} prev={d.revenue_previous} /> az előző időszakhoz
          </>
        }
      >
        <MoneyLines list={d.revenue} reason={reasonFor(err, 'revenue')} />
      </Kpi>
      <Kpi helpKey="mrr" label="MRR (havi ismétlődő)" sub="aktív előfizetések havi díja">
        <MoneyLines list={d.mrr} reason={reasonFor(err, 'mrr')} />
      </Kpi>
      <Kpi
        helpKey="subscriptions"
        label="Aktív előfizetés"
        sub={
          subs
            ? `${subs.new_in_period === null ? DASH : `+${formatCount(subs.new_in_period)}`} új · ${formatCount(subs.canceled_in_period)} lemondva az időszakban`
            : DASH
        }
      >
        {subs?.active === null || subs === null ? <NullValue reason={reasonFor(err, 'subscriptions')} /> : formatCount(subs.active)}
      </Kpi>
      <Kpi
        helpKey="failed"
        label="Sikertelen terhelés"
        sub="elmúlt 30 nap · darabszám és összeg"
      >
        {d.failed_charges_30d === null ? (
          <NullValue reason={reasonFor(err, 'failed_charges_30d')} />
        ) : d.failed_charges_30d.length === 0 ? (
          '0 db'
        ) : (
          <span className="af-money-lines">
            {d.failed_charges_30d.map((m) => (
              <span key={m.currency}>
                {formatCount(m.count)} db <small>{formatMoney(m.amount, m.currency)}</small>
              </span>
            ))}
          </span>
        )}
      </Kpi>
      <Kpi helpKey="unknown" label="Ismeretlen kimenet" tone={typeof unknown === 'number' && unknown > 0 ? 'crit' : undefined} sub="elvárt: 0 · az újrapróbálás zárolva">
        {typeof unknown === 'number' ? `${formatCount(unknown)} db` : <NullValue reason={reasonFor(err, 'unknown_outcome')} />}
      </Kpi>
      <Kpi helpKey="overdue" label="Lejárt tartozás" sub={Array.isArray(d.overdue) ? `${d.overdue.map((m) => formatCount(m.count)).join(' + ') || '0'} előfizetés tartozik` : DASH}>
        <MoneyLines list={d.overdue} reason={reasonFor(err, 'overdue')} />
      </Kpi>
    </section>
  )
}

function SourcesCard({ d }: { d: FinanceOverview }) {
  const groups = new Map<string, SourceEntry[]>()
  for (const s of d.revenue_by_source ?? []) groups.set(s.currency, [...(groups.get(s.currency) ?? []), s])
  return (
    <Card className="af-stack">
      <div>
        <HelpLabel helpKey="sources" as="h2">
          Bevétel forrás szerint
        </HelpLabel>
        <p className="af-sub">
          {d.period.from} – {d.period.to}
        </p>
      </div>
      {d.revenue_by_source === null || d.revenue_by_source === undefined ? (
        <NullValue reason={reasonFor(d.errors, 'revenue_by_source')} />
      ) : groups.size === 0 ? (
        <p className="portal-text-muted">Nincs bevétel az időszakban.</p>
      ) : (
        [...groups].map(([currency, rows]) => {
          const whole = entryFor(d.revenue, currency)?.amount
          return (
            <div key={currency} className="af-stack" data-testid={`sources-${currency}`}>
              <strong>{currency}</strong>
              {rows.map((s) => (
                <div key={s.source} className="af-bar-row">
                  <div className="af-bar-row__head">
                    <span>{SOURCE_LABEL[s.source] ?? s.source}</span>
                    <span className="af-num">{formatMoney(s.amount, currency)}</span>
                  </div>
                  <div className="af-bar">
                    <div style={{ width: `${(whole ? ratioPermille(s.amount, whole) : 0) / 10}%` }} />
                  </div>
                </div>
              ))}
              <div className="af-total">
                <strong>Összesen</strong>
                <strong className="af-num">{whole ? formatMoney(whole, currency) : DASH}</strong>
              </div>
            </div>
          )
        })
      )}
    </Card>
  )
}

function monthlyCurrencies(items: { currency: string; amount: string }[]): string[] {
  const totals = new Map<string, string>()
  for (const i of items) if (!totals.has(i.currency)) totals.set(i.currency, i.amount)
  return [...totals.keys()]
}

function RevenueChartCard({ projectId }: { projectId: string | undefined }) {
  const q = useApiQuery(() => adminFinance.monthlyRevenue(projectId, 12), [projectId])
  const [cur, setCur] = useState<string | null>(null)
  return (
    <Card>
      <HelpLabel helpKey="monthlyChart" as="h2">
        Befolyt bevétel havonta, projektenként
      </HelpLabel>
      <FinanceQuery q={q}>
        {(data) => {
          const currencies = monthlyCurrencies(data.items)
          const currency = cur && currencies.includes(cur) ? cur : currencies[0]
          return (
            <>
              <p className="af-sub">
                Sikeres terhelések · utolsó 12 hónap{data.partial_month ? ` · a(z) ${data.partial_month} hónap részleges` : ''}
              </p>
              {currencies.length > 1 && (
                <Segmented label="Pénznem" options={currencies.map((c) => ({ key: c, label: c }))} value={currency!} onChange={setCur} />
              )}
              {!currency ? (
                <p className="portal-text-muted">Nincs bevétel az elmúlt 12 hónapban.</p>
              ) : (
                <Suspense fallback={<p className="portal-text-muted">Diagram betöltése…</p>}>
                  <RevenueChart data={data} currency={currency} />
                </Suspense>
              )}
            </>
          )
        }}
      </FinanceQuery>
    </Card>
  )
}

function projectStatus(p: ProjectRow): { tone: 'ok' | 'warn' | 'crit' | 'mute'; label: string } {
  if (p.unknown_outcome === null && p.month_close_missing === null) return { tone: 'mute', label: 'Nem ismert' }
  if ((p.unknown_outcome ?? 0) > 0) return { tone: 'crit', label: 'Ismeretlen kimenet' }
  if (p.month_close_missing) return { tone: 'warn', label: 'Zárás elmaradt' }
  return { tone: 'ok', label: 'Rendben' }
}

const num = (v: number | null) => (v === null ? DASH : formatCount(v))

function sumCounts(rows: ProjectRow[], pick: (r: ProjectRow) => number | null): string {
  let t = 0
  for (const r of rows) {
    const v = pick(r)
    if (v === null) return DASH
    t += v
  }
  return formatCount(t)
}

function ProjectsCard({ from, to }: { from: string; to: string }) {
  const fin = useFinance()
  const q = useApiQuery(() => adminFinance.projects({ from, to }), [from, to])
  const [openKey, setOpenKey] = useState<string | null>(null)
  const head = (label: string, helpKey: string) => <ColumnHelpHeader label={label} helpKey={helpKey} openKey={openKey} setOpenKey={setOpenKey} />
  const columns: Column<ProjectRow>[] = [
    {
      key: 'name',
      label: 'Projekt',
      sortValue: (r) => r.name.toLowerCase(),
      format: (_v, r) => (
        <button type="button" className="af-linkbtn" onClick={() => fin.setSelection(r.project_id)}>
          {r.name}
        </button>
      ),
    },
    {
      key: 'revenue',
      label: head('Bevétel', 'colRevenue'),
      numeric: true,
      sortValue: (r) => primarySortKey(r.revenue),
      format: (_v, r) => <MoneyLines list={r.revenue} />,
    },
    {
      key: 'delta',
      label: 'vs. előző',
      numeric: true,
      sortValue: (r) => {
        const first = r.revenue?.[0]
        const p = first ? percentChange(first.amount, entryFor(r.revenue_previous, first.currency)?.amount) : null
        return p
      },
      format: (_v, r) => (r.revenue ? <Delta cur={r.revenue} prev={r.revenue_previous} /> : DASH),
    },
    { key: 'mrr', label: 'MRR', numeric: true, sortValue: (r) => primarySortKey(r.mrr), format: (_v, r) => <MoneyLines list={r.mrr} /> },
    { key: 'subs', label: 'Aktív előfiz.', numeric: true, sortValue: (r) => r.active_subscriptions, format: (_v, r) => num(r.active_subscriptions) },
    { key: 'overdue', label: 'Tartozik', numeric: true, sortValue: (r) => r.overdue_subscriptions, format: (_v, r) => num(r.overdue_subscriptions) },
    { key: 'failed', label: 'Sikertelen terh. (30 nap)', numeric: true, sortValue: (r) => r.failed_charges_30d, format: (_v, r) => num(r.failed_charges_30d) },
    { key: 'close', label: head('Utolsó havi zárás', 'colClose'), sortValue: (r) => r.last_month_close, format: (_v, r) => r.last_month_close ?? DASH },
    {
      key: 'status',
      label: 'Állapot',
      sortValue: (r) => projectStatus(r).label,
      format: (_v, r) => {
        const s = projectStatus(r)
        return <Chip tone={s.tone}>{s.label}</Chip>
      },
    },
  ]
  return (
    <Card>
      <HelpLabel helpKey="projectsTable" as="h2">
        Projektek teljesítménye
      </HelpLabel>
      <p className="af-sub">Kattints egy oszlopfejlécre a rendezéshez · a projekt nevére kattintva a hatókör arra a projektre áll · a pénznemek nem összegezhetők</p>
      <ColumnHelpPanel openKey={openKey} />
      <FinanceQuery q={q}>
        {(data) => (
          <div className="portal-table-scroll" id="projects">
            <Table
              columns={columns}
              rows={data.items}
              rowKey={(r) => r.project_id}
              initialSort={{ key: 'revenue', dir: 'desc' }}
              footer={
                <tr>
                  <td>
                    <strong>Összesen (darabszám)</strong>
                  </td>
                  <td />
                  <td />
                  <td />
                  <td className="af-num af-right">{sumCounts(data.items, (r) => r.active_subscriptions)}</td>
                  <td className="af-num af-right">{sumCounts(data.items, (r) => r.overdue_subscriptions)}</td>
                  <td className="af-num af-right">{sumCounts(data.items, (r) => r.failed_charges_30d)}</td>
                  <td />
                  <td />
                </tr>
              }
            />
          </div>
        )}
      </FinanceQuery>
    </Card>
  )
}

function QueueCard({ projectId }: { projectId: string | undefined }) {
  const q = useApiQuery(() => adminFinance.problems(projectId), [projectId])
  return (
    <Card className="af-stack">
      <HelpLabel helpKey="queue" as="h2">
        Figyelmet igényel
      </HelpLabel>
      <FinanceQuery q={q}>
        {(data) => {
          const total = Object.values(data.counts).reduce<number>((a, n) => a + (typeof n === 'number' ? n : 0), 0)
          return (
            <>
              <div className="af-card__more">
                <Link to="/admin/finance/problems">Mind a {formatCount(total)} tétel →</Link>
              </div>
              {data.items.length === 0 && <p className="portal-text-muted">Nincs nyitott tétel.</p>}
              {data.items.slice(0, 5).map((i, n) => {
                const sev = SEVERITY[i.severity] ?? { label: i.severity, tone: 'mute' as const }
                return (
                  <Link key={`${i.kind}-${i.ref?.id ?? n}`} className="af-queue-row" to={i.email ? `/admin/finance/users?email=${encodeURIComponent(i.email)}` : '/admin/finance/problems'}>
                    <Chip tone={sev.tone}>{sev.label}</Chip>
                    <span className="af-queue-row__main">
                      <strong>{problemTitle(i)}</strong>
                      <span className="af-sub">{[i.email, i.project_name, shortDate(i.since)].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="af-num">{i.amount ? formatMoney(i.amount, i.currency) : DASH}</span>
                  </Link>
                )
              })}
            </>
          )
        }}
      </FinanceQuery>
    </Card>
  )
}

function UpcomingCard({ projectId }: { projectId: string | undefined }) {
  const q = useApiQuery(() => adminFinance.upcoming(projectId, 7), [projectId])
  return (
    <Card className="af-stack">
      <div>
        <HelpLabel helpKey="upcoming" as="h2">
          Várható terhelések – következő 7 nap
        </HelpLabel>
      </div>
      <FinanceQuery q={q}>
        {(data) => {
          const max = Math.max(1, ...data.days.map((d) => d.count))
          return (
            <>
              <p className="af-sub">
                Esedékes megújítások ·{' '}
                {data.total.length === 0 ? '0 db' : data.total.map((t) => `${formatCount(t.count)} db · ${formatMoney(t.amount, t.currency)}`).join(' + ')}
              </p>
              {data.total_covered_by_balance && data.total_to_charge_to_card && (
                <p className="af-sub" data-testid="upcoming-split">
                  Egyenlegből: <span className="af-num">{moneyJoin(data.total_covered_by_balance)}</span> · kártyára: <span className="af-num">{moneyJoin(data.total_to_charge_to_card)}</span>
                </p>
              )}
              {data.days.map((d) => (
                <div key={d.date} className="af-upcoming-row">
                  <span>{shortDate(d.date)}</span>
                  <div className="af-bar">
                    <div style={{ width: `${(d.count / max) * 100}%` }} />
                  </div>
                  <span className="af-num af-right">{formatCount(d.count)} db</span>
                  <span className="af-num af-right">{d.amounts.length === 0 ? DASH : d.amounts.map((a) => formatMoney(a.amount, a.currency)).join(' + ')}</span>
                  {d.covered_by_balance && d.to_charge_to_card && (
                    <span className="af-upcoming-row__split af-sub">
                      egyenlegből {moneyJoin(d.covered_by_balance)} · kártyára {moneyJoin(d.to_charge_to_card)}
                    </span>
                  )}
                </div>
              ))}
            </>
          )
        }}
      </FinanceQuery>
    </Card>
  )
}

function CreditsCard({ projectId }: { projectId: string | undefined }) {
  const q = useApiQuery(() => adminFinance.credits(projectId), [projectId])
  const columns: Column<CreditCurrencyRow>[] = [
    { key: 'currency', label: 'Pénznem', format: (_v, r) => <strong>{r.currency}</strong> },
    { key: 'balance', label: 'Egyenleg', numeric: true, sortValue: (r) => decimalSortKey(r.balance), format: (_v, r) => formatAmount(r.balance) },
    { key: 'accounts', label: 'Számlák', numeric: true, sortValue: (r) => r.accounts, format: (_v, r) => (r.accounts === null ? DASH : formatCount(r.accounts)) },
    {
      key: 'neg',
      label: 'Negatív',
      numeric: true,
      sortValue: (r) => r.negative_accounts,
      format: (_v, r) =>
        r.negative_accounts === null ? DASH : r.negative_accounts > 0 ? <Chip tone="crit">{formatCount(r.negative_accounts)}</Chip> : formatCount(r.negative_accounts),
    },
  ]
  return (
    <Card className="af-stack">
      <div>
        <HelpLabel helpKey="credits" as="h2">
          Kredit-egyenlegek coinonként
        </HelpLabel>
        <p className="af-sub">Minden pénznem külön – nincs átváltás, nincs közös összeg</p>
      </div>
      <FinanceQuery q={q}>
        {(data) => (
          <div className="portal-table-scroll">
            <Table columns={columns} rows={data.items} rowKey={(r) => r.currency} initialSort={{ key: 'currency', dir: 'asc' }} />
          </div>
        )}
      </FinanceQuery>
    </Card>
  )
}

export function OverviewPage() {
  const fin = useFinance()
  const [period, setPeriod] = useState<PeriodKey>('month')
  const { from, to } = periodRange(period)
  const q = useApiQuery(() => adminFinance.overview(fin.projectId, { from, to }), [fin.projectId, from, to])
  const scopeName = fin.scope?.projects.find((p) => p.id === fin.projectId)?.name ?? 'Minden projekt'
  return (
    <>
      <PageHead
        title="Pénzügyi áttekintés"
        sub={
          q.data ? (
            <>
              {scopeName} · {q.data.period.from} – {q.data.period.to} · adat frissítve {q.data.generated_at ? formatDateTime(q.data.generated_at) : DASH}
            </>
          ) : (
            scopeName
          )
        }
      >
        <Segmented label="Időszak" options={PERIODS} value={period} onChange={setPeriod} />
      </PageHead>
      {q.error ? (
        <FinanceErrorNotice error={q.error} />
      ) : !q.data ? (
        <p className="portal-text-muted">Betöltés…</p>
      ) : (
        <KpiGrid d={q.data} />
      )}
      <section className="af-row-chart">
        <RevenueChartCard projectId={fin.projectId} />
        {q.data && <SourcesCard d={q.data} />}
      </section>
      <ProjectsCard from={from} to={to} />
      <section className="af-row-3">
        <QueueCard projectId={fin.projectId} />
        <UpcomingCard projectId={fin.projectId} />
        <CreditsCard projectId={fin.projectId} />
      </section>
    </>
  )
}
